/**
 * The face of the person a customer is about to hand their pet to.
 *
 * Shown wherever a walker or trainer is being chosen or booked. A photo is mandatory for
 * those modules now, but it only became mandatory at submit — every walker and trainer
 * verified before that has no photo, and they are live to customers today. So the fallback
 * is not a nicety: it is the common case until those vendors are backfilled, and it has to
 * look deliberate rather than broken.
 *
 * Initials on a tinted circle, not a generic silhouette: a silhouette reads as "failed to
 * load" and invites a reload, whereas initials read as "no photo yet" and stay legible at
 * the small sizes a booking header uses.
 */
import { AuthImage } from '@/components/auth-image';

/** Up to two initials from a business or person name. */
function initials_of(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const SIZES = {
  sm: 'h-10 w-10 text-xs',
  md: 'h-14 w-14 text-sm',
  lg: 'h-20 w-20 text-base',
} as const;

export function ProviderAvatar({
  /** `profile_photo_path` from a discovery payload. Null when the vendor never supplied one. */
  path,
  name,
  size = 'md',
  'data-testid': testid,
}: {
  path: string | null | undefined;
  name: string | null | undefined;
  size?: keyof typeof SIZES;
  'data-testid'?: string;
}) {
  /*
   * A white ring, because this also sits on top of photography (the browse card scrim) where an
   * unringed circle bleeds into whatever is behind it.
   */
  const shape = `${SIZES[size]} shrink-0 overflow-hidden rounded-full ring-2 ring-white/80`;

  if (!path) {
    return (
      <div
        className={`${shape} flex items-center justify-center bg-primary/10 font-semibold text-primary`}
        // Not aria-hidden: "no photo" is information a customer should get from the page,
        // not something only sighted users infer from a coloured circle.
        aria-label={`${name ?? 'Provider'} — no photo provided`}
        title={`${name ?? 'Provider'} — no photo provided`}
        data-testid={testid}
        data-has-photo="false"
      >
        {initials_of(name)}
      </div>
    );
  }

  return (
    <div className={shape} data-testid={testid} data-has-photo="true">
      <AuthImage
        // `thumb` is the variant the upload pipeline generates for avatars; the header never
        // needs the full-size bytes and a booking screen is often on mobile data.
        path={`${path}?variant=thumb`}
        alt={`${name ?? 'Provider'}'s profile photo`}
        className="h-full w-full object-cover"
      />
    </div>
  );
}

/** True for the modules where the vendor is a person who comes to the customer. */
export function is_at_your_door(module_types: string[] | undefined): boolean {
  return (module_types ?? []).some((m) => m === 'WALKER' || m === 'TRAINER');
}
